import { useState } from 'react';
import { Button } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router';
import { GenerateProductsFromImagesResponseDto } from '@tiendanube/common';
import { AiFeature, Domain } from '@tiendanube/common/src/enums';
import useLayoutScroll from 'App/components/AppLayout/useLayoutScroll';
import { useToastProgress } from 'App/components/ToastProgressContext';
import { CATALOG_UPLOAD_VIDEOS } from 'App/featuresFlags';
import { useHasTags, useNavegate } from 'App/hooks';
import { HeaderTop, HeaderContent, IonPageStratus } from 'commons/components';
import { AiIcon } from 'commons/components/GenerateWithAiLink';
import { useToast, useDocumentTitle } from 'commons/hooks';
import {
  trackingAiGenerateGenerateClick,
  trackingAiGenerateUseContentClick,
} from 'domains/Ai/tracking';
import { useGetLanguage, useHasShippingMultiCD } from 'domains/Auth/hooks';
import { CATALOG_ROUTES } from 'domains/Catalog/catalogRoutes';
import {
  ImageGalleryGrid,
  ProductsIdentificationResultsTable,
} from './components';
import { MAX_IMAGES_TO_LOAD } from './constants';
import { useGenerateProductsFromImagesMutation } from '../../productsApi/productsFromImagesApi';
import { ImageGalleryState } from '../components/ImageGallery/ImageGallery';
import {
  useCreateProduct,
  useProductForm,
  useFetchProductVariantsMetafields,
} from '../hooks';
import { ProductFormState } from '../hooks/useProductForm/types';
import { productDefault } from '../NewProductPage/constants';
import { createProductDigest } from '../NewProductPage/productFormUtils';

export type ProductsFromImages =
  GenerateProductsFromImagesResponseDto['products'];

function NewProductFromImagesPage(): JSX.Element {
  const { selectFirstOptionMetafields } = useFetchProductVariantsMetafields();
  const hasShippingMultiCD = useHasShippingMultiCD();
  const history = useHistory();
  const language = useGetLanguage();
  const { scrollToTop } = useLayoutScroll();
  const { goBack } = useNavegate();
  const [hasUploadVideos] = useHasTags([CATALOG_UPLOAD_VIDEOS]);
  const { t } = useTranslation(Domain.CATALOG);
  const { addProduct } = useCreateProduct();
  const { addToast } = useToast();
  const { addToastProgress, closeToastProgress } = useToastProgress();
  const title = t('products.fromImages.title');

  const [products, setProducts] = useState<ProductsFromImages>([]);
  const [showResultsTable, setShowResultsTable] = useState(false);
  const [isGeneratingProducts, setIsGeneratingProducts] = useState(false);

  const [generateProductsFromImages, { isLoading: isIdentifying }] =
    useGenerateProductsFromImagesMutation();

  useDocumentTitle(title);

  const onCreateProduct = async (data: Partial<ProductFormState>) => {
    const params = createProductDigest({
      product: data,
      language,
      hasShippingMultiCD,
      hasMedia: hasUploadVideos,
    });

    const newProduct = await addProduct(params);

    history.replace(
      generatePath(CATALOG_ROUTES.editProduct, { id: newProduct.id }),
    );
  };

  const onSuccess = () => {
    closeToastProgress();
  };

  const onError = () => {
    closeToastProgress();
    addToast({
      label: t('products.toast.createdError'),
      appearance: 'danger',
    });
  };

  const { values, handleChangeImages } = useProductForm({
    initialValues: productDefault,
    selectFirstOptionMetafields,
    onSave: onCreateProduct,
    onError,
    onSuccess,
  });

  const handleGenerateProducts = async () => {
    trackingAiGenerateUseContentClick(
      {
        feature: AiFeature.AI_PRODUCTS_FROM_IMAGES,
      },
      {
        number_of_images: values.images.length.toString(),
        number_of_products: products.length.toString(),
      },
    );

    setIsGeneratingProducts(true);
    addToastProgress({
      label: t('products.fromImages.generating'),
    });

    try {
      const addProductPromises = products.map(async (product) => {
        const images: ImageGalleryState[] = product.images.map((img) => ({
          id: img.id,
          alt: { [language]: '' },
          isLoading: false,
          isError: false,
          src: img.url,
          name: '',
          isEdited: false,
          type: '',
          size: 0,
          mimeType: '',
          mediaType: 'image',
          videoUrl: '',
        }));

        const productData: Partial<ProductFormState> = {
          name: { [language]: product.name },
          description: { [language]: product.description },
          images,
          media: images,
        };

        const params = createProductDigest({
          product: productData,
          language,
          hasShippingMultiCD,
          hasMedia: hasUploadVideos,
        });

        return addProduct(params);
      });

      await Promise.all(addProductPromises);

      onSuccess();
      history.replace(CATALOG_ROUTES.productList);
    } catch (error) {
      onError();
    } finally {
      setIsGeneratingProducts(false);
    }
  };

  const handleIdentifyProducts = async () => {
    trackingAiGenerateGenerateClick({
      feature: AiFeature.AI_PRODUCTS_FROM_IMAGES,
    });
    if (values.images.length > MAX_IMAGES_TO_LOAD) {
      scrollToTop('smooth');
      return;
    }

    try {
      const images = values.images
        .filter((image) => Boolean(image.id) && Boolean(image.publicUrl))
        .map(({ id, publicUrl }) => ({
          id: String(id),
          url: String(publicUrl),
        }));

      const { products } = await generateProductsFromImages({
        images,
        lang: language,
      }).unwrap();

      setProducts(products || []);
      setShowResultsTable(true);
    } catch (error) {
      addToast({
        label: t('products.fromImages.identificationError'),
        appearance: 'danger',
      });
      setShowResultsTable(false);
    }
  };

  const hasAllImagesLoaded = values.images.every(
    (image) => image.isLoading === false,
  );

  const backNavigation = {
    onClick: goBack,
  };

  const canIdentifyProducts =
    hasAllImagesLoaded &&
    values.images.length > 0 &&
    products.length === 0 &&
    !isIdentifying;

  return (
    <IonPageStratus
      headerTop={<HeaderTop navigation={backNavigation} />}
      headerContent={
        <HeaderContent
          title={title}
          subtitle={t('products.fromImages.description')}
        />
      }
      width="small"
    >
      {products.length === 0 && (
        <ImageGalleryGrid
          images={values.images}
          onChange={handleChangeImages}
          isIdentifying={isIdentifying}
        />
      )}
      {canIdentifyProducts && (
        <Button onClick={handleIdentifyProducts}>
          <AiIcon />
          {t('products.fromImages.identifyProducts')}
        </Button>
      )}
      <ProductsIdentificationResultsTable
        products={products}
        isVisible={showResultsTable}
        onProductsChange={setProducts}
      />
      {products.length > 0 && (
        <Button
          onClick={handleGenerateProducts}
          disabled={isGeneratingProducts}
        >
          <AiIcon /> {t('products.fromImages.generateProducts')}
        </Button>
      )}
    </IonPageStratus>
  );
}

export default NewProductFromImagesPage;
